import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Inbox, FileText, Search, Upload, Camera, CheckCircle2, ArrowRight, RefreshCw,
  Trash2, Download, Eye, ChevronRight, X, MoreHorizontal, ArrowLeft,
  Receipt, CreditCard, Building2, Tag, Users, FolderOpen, Zap, Archive,
  FileSpreadsheet, Image, Clock, AlertCircle, Check, Link2, Unlink,
  Settings2, Brain, Send, Filter, ChevronDown, Loader2, QrCode, Smartphone
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ==================== STATUS BADGES ====================
const StatusBadge = ({ status }) => {
  const styles = {
    inbox: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    processed: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
    review: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    matched: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    ready_to_sync: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
    synced: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    extraction_failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    unmatched: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  };
  return (
    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide", styles[status] || styles.inbox)}>
      {(status || 'inbox').replace(/_/g, ' ')}
    </span>
  );
};

const MatchBadge = ({ status, score }) => {
  const styles = {
    'auto-matched': 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    'suggested': 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    'unmatched': 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    'manually_linked': 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  };
  return (
    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold", styles[status] || styles.unmatched)}>
      {status?.replace(/_/g, ' ') || 'unmatched'}{score > 0 ? ` (${score}%)` : ''}
    </span>
  );
};

import { useOrg } from '../contexts/OrgContext';

// ==================== METRIC CARD ====================
const MetricCard = ({ label, value, icon: Icon, color = "text-foreground", onClick }) => (
  <button onClick={onClick} className={cn("flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/40 transition-colors text-left w-full", onClick && "cursor-pointer")}>
    {Icon && <div className={cn("p-2 rounded-md bg-muted/60", color)}><Icon className="w-4 h-4" /></div>}
    <div>
      <p className="text-lg font-bold leading-tight">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  </button>
);

// ==================== MAIN PAGE ====================
export default function ExpenseCapturePage() {
  const navigate = useNavigate();
  const { orgId, handle409 } = useOrg();
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [selectedReceipts, setSelectedReceipts] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Reference data from Orka Finance
  const [refAccounts, setRefAccounts] = useState([]);
  const [refCategories, setRefCategories] = useState([]);
  const [refVendors, setRefVendors] = useState([]);
  const [refProjects, setRefProjects] = useState([]);
  const [refDepartments, setRefDepartments] = useState([]);
  const [refBusinessUnits, setRefBusinessUnits] = useState([]);

  // Memory & Config
  const [memoryEntries, setMemoryEntries] = useState([]);
  const [captureConfig, setCaptureConfig] = useState(null);
  const [candidates, setCandidates] = useState(null); // { transaction_id, candidates[] }

  // Modals
  const [uploadModal, setUploadModal] = useState(null); // 'statement' | 'receipt' | null
  const [detailModal, setDetailModal] = useState(null); // {type, item}
  const [bulkEditModal, setBulkEditModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({});
  const [columnMapModal, setColumnMapModal] = useState(null); // statement object
  const [mobileSessionModal, setMobileSessionModal] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionPolling, setSessionPolling] = useState(false);

  const fileInputRef = useRef(null);
  const receiptInputRef = useRef(null);

  // ==================== DATA FETCHING ====================
  const fetchWorkspace = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await fetch(`${API}/api/finance/workspaces?org_id=${orgId}`, { credentials: 'include' });
      if (r.status === 409) { handle409(); return; }
      if (r.ok) {
        const data = await r.json();
        if (data.length > 0) setActiveWorkspace(data[0]);
      }
    } catch {}
  }, [orgId, handle409]);

  const fetchStats = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/stats`, { credentials: 'include' });
      if (r.ok) setStats(await r.json());
    } catch {}
  }, [activeWorkspace]);

  const fetchTransactions = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions`, { credentials: 'include' });
      if (r.ok) setTransactions(await r.json());
    } catch {}
  }, [activeWorkspace]);

  const fetchReceipts = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/receipts`, { credentials: 'include' });
      if (r.ok) setReceipts(await r.json());
    } catch {}
  }, [activeWorkspace]);

  const fetchStatements = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/statements`, { credentials: 'include' });
      if (r.ok) setStatements(await r.json());
    } catch {}
  }, [activeWorkspace]);

  const fetchRefData = useCallback(async () => {
    if (!activeWorkspace) return;
    const wid = activeWorkspace.workspace_id;
    const fj = async (path) => { try { const r = await fetch(`${API}/api/finance/ref/${wid}/${path}`, { credentials: 'include' }); return r.ok ? await r.json() : []; } catch { return []; } };
    const [accts, cats, vnds, projs, depts, bus] = await Promise.all([
      fj('expense-accounts'), fj('expense-categories'), fj('vendors'), fj('projects'),
      (async () => { try { const r = await fetch(`${API}/api/org/departments`, { credentials: 'include' }); return r.ok ? await r.json() : []; } catch { return []; } })(),
      fj('business-units'),
    ]);
    setRefAccounts(accts); setRefCategories(cats); setRefVendors(vnds); setRefProjects(projs); setRefDepartments(depts); setRefBusinessUnits(bus);
  }, [activeWorkspace]);

  const fetchMemory = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/memory`, { credentials: 'include' });
      if (r.ok) setMemoryEntries(await r.json());
    } catch {}
  }, [activeWorkspace]);

  const fetchConfig = useCallback(async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/config`, { credentials: 'include' });
      if (r.ok) setCaptureConfig(await r.json());
    } catch {}
  }, [activeWorkspace]);

  const refreshAll = useCallback(() => {
    fetchStats(); fetchTransactions(); fetchReceipts(); fetchStatements();
  }, [fetchStats, fetchTransactions, fetchReceipts, fetchStatements]);

  useEffect(() => { fetchWorkspace(); }, [fetchWorkspace]);
  useEffect(() => {
    if (activeWorkspace) { refreshAll(); fetchRefData(); fetchMemory(); fetchConfig(); }
  }, [activeWorkspace, refreshAll, fetchRefData, fetchMemory, fetchConfig]);

  // ==================== FILTERED DATA ====================
  const filteredTransactions = useMemo(() => {
    switch (activeTab) {
      case 'inbox': return transactions.filter(t => t.status === 'inbox');
      case 'review': return transactions.filter(t => t.status === 'review' || t.match_status === 'suggested');
      case 'matched': return transactions.filter(t => t.status === 'matched');
      case 'ready': return transactions.filter(t => t.status === 'ready_to_sync');
      case 'synced': return transactions.filter(t => t.status === 'synced');
      default: return transactions;
    }
  }, [transactions, activeTab]);

  const filteredReceipts = useMemo(() => {
    switch (activeTab) {
      case 'inbox': return receipts.filter(r => r.status === 'inbox');
      case 'review': return receipts.filter(r => r.status === 'review' || r.status === 'processed');
      case 'matched': return receipts.filter(r => r.status === 'matched');
      default: return receipts;
    }
  }, [receipts, activeTab]);

  // Category helpers
  const expenseTypes = useMemo(() => {
    const custom = (refCategories || []).filter(c => !c.parent_code).map(c => c.name);
    return [...new Set(custom)];
  }, [refCategories]);

  const expenseSubTypes = useMemo(() => {
    return (refCategories || []).filter(c => c.parent_code).map(c => ({ name: c.name, parent: c.parent_code }));
  }, [refCategories]);

  // ==================== ACTIONS ====================
  const handleStatementUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeWorkspace) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('account_paid_from', bulkForm.account_paid_from || '');
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/statements/upload`, {
        method: 'POST', credentials: 'include', body: form,
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Imported ${data.transactions_created} transactions from ${file.name}`);
        // Show column mapping if headers detected
        if (data.statement) setColumnMapModal(data.statement);
        refreshAll();
      } else {
        toast.error('Upload failed');
      }
    } catch { toast.error('Upload error'); }
    setLoading(false);
    setUploadModal(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleReceiptUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !activeWorkspace) return;
    setLoading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        form.append('session_id', `desktop_${Date.now()}`);
        const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/receipts/upload`, {
          method: 'POST', credentials: 'include', body: form,
        });
        if (r.ok) uploaded++;
      } catch {}
    }
    toast.success(`Uploaded ${uploaded} receipt(s)`);
    refreshAll();
    setLoading(false);
    setUploadModal(null);
    if (receiptInputRef.current) receiptInputRef.current.value = '';
  };

  const extractReceipt = async (receiptId) => {
    if (!activeWorkspace) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/receipts/${receiptId}/extract`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        const data = await r.json();
        toast.success('Receipt data extracted');
        refreshAll();
        return data;
      } else {
        toast.error('Extraction failed');
      }
    } catch { toast.error('Error'); }
    setLoading(false);
  };

  const runReconciliation = async () => {
    if (!activeWorkspace) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/reconcile`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Found ${data.matches_found} matches`);
        refreshAll();
      }
    } catch { toast.error('Reconciliation failed'); }
    setLoading(false);
  };

  const updateTransaction = async (txnId, updates) => {
    if (!activeWorkspace) return;
    try {
      await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/${txnId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      refreshAll();
    } catch {}
  };

  const bulkUpdateTransactions = async (updates) => {
    if (!activeWorkspace || selectedTxns.size === 0) return;
    try {
      await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/bulk-update`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedTxns), updates }),
      });
      toast.success(`Updated ${selectedTxns.size} transactions`);
      setSelectedTxns(new Set());
      setBulkEditModal(false);
      setBulkForm({});
      refreshAll();
    } catch { toast.error('Bulk update failed'); }
  };

  const syncToFinance = async (ids = null) => {
    if (!activeWorkspace) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/sync`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction_ids: ids || [] }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Synced ${data.synced} transactions to Orka Finance`);
        refreshAll();
      }
    } catch { toast.error('Sync failed'); }
    setLoading(false);
  };

  const categorizeTransaction = async (txnId) => {
    if (!activeWorkspace) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/${txnId}/categorize`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        toast.success('AI categorization complete');
        refreshAll();
      }
    } catch { toast.error('Categorization failed'); }
    setLoading(false);
  };

  const manualLink = async (txnId, receiptId) => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/${txnId}/link`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ receipt_id: receiptId }),
      });
      if (r.ok) { toast.success('Receipt linked'); refreshAll(); setCandidates(null); setDetailModal(null); }
    } catch { toast.error('Link failed'); }
  };

  const unlinkMatch = async (txnId) => {
    if (!activeWorkspace) return;
    try {
      await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/${txnId}/unlink`, {
        method: 'POST', credentials: 'include',
      });
      toast.success('Match unlinked'); refreshAll();
    } catch { toast.error('Unlink failed'); }
  };

  const confirmMatch = async (txnId) => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/${txnId}/confirm`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Match confirmed. ${data.patterns_saved} patterns saved to memory.`);
        refreshAll(); fetchMemory();
      }
    } catch { toast.error('Confirm failed'); }
  };

  const loadCandidates = async (txnId) => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/transactions/${txnId}/candidates`, { credentials: 'include' });
      if (r.ok) setCandidates(await r.json());
    } catch {}
  };

  const deleteMemoryEntry = async (memId) => {
    if (!activeWorkspace) return;
    try {
      await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/memory/${memId}`, {
        method: 'DELETE', credentials: 'include',
      });
      toast.success('Memory entry deleted');
      fetchMemory();
    } catch {}
  };

  const resetAllMemory = async () => {
    if (!activeWorkspace) return;
    if (!window.confirm('Reset all learned patterns? This cannot be undone.')) return;
    try {
      await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/memory`, {
        method: 'DELETE', credentials: 'include',
      });
      toast.success('All memory reset');
      fetchMemory();
    } catch {}
  };

  const saveConfig = async (updates) => {
    if (!activeWorkspace) return;
    try {
      await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      toast.success('Config saved');
      fetchConfig();
    } catch {}
  };

  // Mobile session handlers
  const createMobileSession = async () => {
    if (!activeWorkspace) return;
    try {
      const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/sessions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `Mobile Capture ${new Date().toLocaleDateString()}` }),
      });
      if (r.ok) {
        const session = await r.json();
        setActiveSession(session);
        setMobileSessionModal(true);
        setSessionPolling(true);
      }
    } catch { toast.error('Failed to create session'); }
  };

  // Poll active session for new receipts
  useEffect(() => {
    if (!sessionPolling || !activeSession || !activeWorkspace) return;
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/expense-capture/${activeWorkspace.workspace_id}/sessions/${activeSession.session_id}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setActiveSession(prev => ({ ...prev, ...data }));
          if (data.receipt_count > (activeSession.receipt_count || 0)) {
            refreshAll();
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [sessionPolling, activeSession, activeWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeMobileSession = () => {
    setMobileSessionModal(false);
    setSessionPolling(false);
    refreshAll();
  };

  const getMobileCaptureUrl = () => {
    if (!activeWorkspace || !activeSession) return '';
    const base = window.location.origin;
    return `${base}/capture?w=${activeWorkspace.workspace_id}&s=${activeSession.session_id}`;
  };

  // ==================== RENDER ====================
  if (!activeWorkspace) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  const tabItems = [
    { value: 'inbox', label: 'Inbox', icon: Inbox, count: (stats?.transactions?.inbox || 0) + (stats?.receipts?.inbox || 0) },
    { value: 'review', label: 'Review', icon: AlertCircle, count: stats?.transactions?.review || 0 },
    { value: 'matched', label: 'Matched', icon: Link2, count: stats?.transactions?.matched || 0 },
    { value: 'ready', label: 'Ready to Sync', icon: Send, count: stats?.transactions?.ready_to_sync || 0 },
    { value: 'synced', label: 'Synced', icon: Archive, count: stats?.transactions?.synced || 0 },
    { value: 'config', label: 'Config', icon: Settings2 },
  ];

  return (
    <div className="space-y-4 max-w-[1600px] mx-auto" data-testid="expense-capture-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/finance')} className="p-1.5 rounded-md hover:bg-muted" data-testid="back-to-finance-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-lg font-bold tracking-tight">Expense Capture & Reconciliation</h1>
            <p className="text-xs text-muted-foreground">Ingest statements, capture receipts, reconcile and sync to Orka Finance</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={createMobileSession} data-testid="mobile-capture-btn">
            <Smartphone className="w-3.5 h-3.5 mr-1.5" />Mobile Capture
          </Button>
          <Button size="sm" variant="outline" onClick={() => setUploadModal('statement')} data-testid="upload-statement-btn">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Upload Statement
          </Button>
          <Button size="sm" variant="outline" onClick={() => setUploadModal('receipt')} data-testid="upload-receipt-btn">
            <Camera className="w-3.5 h-3.5 mr-1.5" />Upload Receipt
          </Button>
          <Button size="sm" variant="outline" onClick={runReconciliation} disabled={loading} data-testid="reconcile-btn">
            <Zap className="w-3.5 h-3.5 mr-1.5" />{loading ? 'Processing...' : 'Reconcile'}
          </Button>
          <Button size="sm" onClick={() => syncToFinance()} disabled={loading} data-testid="sync-all-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" />Sync Ready
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-6 gap-2" data-testid="stats-row">
          <MetricCard label="Inbox" value={stats.transactions.inbox + stats.receipts.inbox} icon={Inbox} onClick={() => setActiveTab('inbox')} />
          <MetricCard label="Need Review" value={stats.transactions.review} icon={AlertCircle} color="text-amber-600" onClick={() => setActiveTab('review')} />
          <MetricCard label="Matched" value={stats.transactions.matched} icon={Link2} color="text-green-600" onClick={() => setActiveTab('matched')} />
          <MetricCard label="Ready to Sync" value={stats.transactions.ready_to_sync} icon={Send} color="text-indigo-600" onClick={() => setActiveTab('ready')} />
          <MetricCard label="Synced" value={stats.transactions.synced} icon={Archive} color="text-emerald-600" onClick={() => setActiveTab('synced')} />
          <MetricCard label="Receipts" value={stats.receipts.total} icon={Receipt} />
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-9">
          {tabItems.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs gap-1.5" data-testid={`tab-${t.value}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
              {t.count !== undefined && t.count > 0 && (
                <span className="ml-1 px-1.5 py-0 rounded-full bg-muted text-[10px] font-semibold">{t.count}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ==================== INBOX TAB ==================== */}
        <TabsContent value="inbox" className="space-y-4">
          {/* Transactions Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <CreditCard className="w-4 h-4" />Statement Transactions
                <span className="text-xs text-muted-foreground font-normal">({filteredTransactions.length})</span>
              </h3>
              {selectedTxns.size > 0 && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setBulkForm({}); setBulkEditModal(true); }}>
                    Bulk Edit ({selectedTxns.size})
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => bulkUpdateTransactions({ status: 'ready_to_sync' })}>
                    Mark Ready to Sync
                  </Button>
                </div>
              )}
            </div>
            <TransactionTable
              transactions={filteredTransactions}
              selected={selectedTxns}
              onSelect={setSelectedTxns}
              onView={(t) => setDetailModal({ type: 'transaction', item: t })}
              onUpdate={updateTransaction}
              onCategorize={categorizeTransaction}
              accounts={refAccounts}
              expenseTypes={expenseTypes}
            />
          </div>

          {/* Receipts Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Receipt className="w-4 h-4" />Receipts
                <span className="text-xs text-muted-foreground font-normal">({filteredReceipts.length})</span>
              </h3>
            </div>
            <ReceiptGrid
              receipts={filteredReceipts}
              onView={(r) => setDetailModal({ type: 'receipt', item: r })}
              onExtract={extractReceipt}
              loading={loading}
            />
          </div>
        </TabsContent>

        {/* ==================== REVIEW TAB ==================== */}
        <TabsContent value="review" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Items Needing Review</h3>
            <div className="flex gap-2">
              {selectedTxns.size > 0 && (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setBulkForm({}); setBulkEditModal(true); }}>
                    Bulk Edit ({selectedTxns.size})
                  </Button>
                  <Button size="sm" onClick={() => {
                    Array.from(selectedTxns).forEach(id => confirmMatch(id));
                    setSelectedTxns(new Set());
                  }} data-testid="confirm-selected-btn">
                    Confirm Selected
                  </Button>
                </>
              )}
            </div>
          </div>
          <TransactionTable
            transactions={filteredTransactions}
            selected={selectedTxns}
            onSelect={setSelectedTxns}
            onView={(t) => setDetailModal({ type: 'transaction', item: t })}
            onUpdate={updateTransaction}
            onCategorize={categorizeTransaction}
            onConfirm={confirmMatch}
            onUnlink={unlinkMatch}
            onFindCandidates={(t) => { loadCandidates(t.transaction_id); setDetailModal({ type: 'candidates', item: t }); }}
            accounts={refAccounts}
            expenseTypes={expenseTypes}
            showMatchInfo
            showActions
          />
        </TabsContent>

        {/* ==================== MATCHED TAB ==================== */}
        <TabsContent value="matched" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Matched Transactions</h3>
            {selectedTxns.size > 0 && (
              <Button size="sm" onClick={() => bulkUpdateTransactions({ status: 'ready_to_sync' })}>
                Move to Ready ({selectedTxns.size})
              </Button>
            )}
          </div>
          <TransactionTable
            transactions={filteredTransactions}
            selected={selectedTxns}
            onSelect={setSelectedTxns}
            onView={(t) => setDetailModal({ type: 'transaction', item: t })}
            onUpdate={updateTransaction}
            onConfirm={confirmMatch}
            onUnlink={unlinkMatch}
            accounts={refAccounts}
            expenseTypes={expenseTypes}
            showMatchInfo
          />
        </TabsContent>

        {/* ==================== READY TO SYNC TAB ==================== */}
        <TabsContent value="ready" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Ready to Sync to Orka Finance</h3>
            <div className="flex gap-2">
              {selectedTxns.size > 0 && (
                <Button size="sm" onClick={() => syncToFinance(Array.from(selectedTxns))}>
                  Sync Selected ({selectedTxns.size})
                </Button>
              )}
              <Button size="sm" onClick={() => syncToFinance()} disabled={loading}>
                <Send className="w-3.5 h-3.5 mr-1.5" />Sync All Ready
              </Button>
            </div>
          </div>
          <TransactionTable
            transactions={filteredTransactions}
            selected={selectedTxns}
            onSelect={setSelectedTxns}
            onView={(t) => setDetailModal({ type: 'transaction', item: t })}
            onUpdate={updateTransaction}
            accounts={refAccounts}
            expenseTypes={expenseTypes}
          />
        </TabsContent>

        {/* ==================== SYNCED TAB ==================== */}
        <TabsContent value="synced" className="space-y-4">
          <h3 className="text-sm font-semibold">Synced to Orka Finance</h3>
          <TransactionTable
            transactions={filteredTransactions}
            selected={new Set()}
            onSelect={() => {}}
            onView={(t) => setDetailModal({ type: 'transaction', item: t })}
            onUpdate={() => {}}
            accounts={refAccounts}
            expenseTypes={expenseTypes}
            readOnly
          />
        </TabsContent>

        {/* ==================== CONFIG TAB ==================== */}
        <TabsContent value="config" className="space-y-4">
          <ConfigPanel
            accounts={refAccounts}
            categories={refCategories}
            vendors={refVendors}
            projects={refProjects}
            departments={refDepartments}
            businessUnits={refBusinessUnits}
            statements={statements}
            memoryEntries={memoryEntries}
            captureConfig={captureConfig}
            onDeleteMemory={deleteMemoryEntry}
            onResetMemory={resetAllMemory}
            onSaveConfig={saveConfig}
          />
        </TabsContent>
      </Tabs>

      {/* ==================== UPLOAD STATEMENT MODAL ==================== */}
      <Dialog open={uploadModal === 'statement'} onOpenChange={() => setUploadModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" />Upload Bank/Card Statement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Supported formats: PDF, CSV, XLSX. The system will extract transaction rows automatically.</p>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Account Paid From</label>
              <Select value={bulkForm.account_paid_from || ''} onValueChange={v => setBulkForm(p => ({ ...p, account_paid_from: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select account..." /></SelectTrigger>
                <SelectContent>
                  {refAccounts.map(a => <SelectItem key={a.account_id || a.name} value={a.name}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Click to select statement file</p>
              <p className="text-[10px] text-muted-foreground mt-1">PDF, CSV, or XLSX</p>
            </div>
            <input ref={fileInputRef} type="file" accept=".pdf,.csv,.xlsx,.xls" className="hidden" onChange={handleStatementUpload} />
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== UPLOAD RECEIPT MODAL ==================== */}
      <Dialog open={uploadModal === 'receipt'} onOpenChange={() => setUploadModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5" />Upload Receipts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Upload receipt images or PDFs. You can select multiple files at once.</p>
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => receiptInputRef.current?.click()}>
              <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Click to select receipt files</p>
              <p className="text-[10px] text-muted-foreground mt-1">JPG, PNG, PDF (multiple allowed)</p>
            </div>
            <input ref={receiptInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" multiple className="hidden" onChange={handleReceiptUpload} />
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== DETAIL MODAL ==================== */}
      {detailModal && (
        <DetailModal
          type={detailModal.type}
          item={detailModal.item}
          onClose={() => { setDetailModal(null); setCandidates(null); }}
          onUpdate={updateTransaction}
          onExtract={extractReceipt}
          onConfirm={confirmMatch}
          onUnlink={unlinkMatch}
          onLink={manualLink}
          candidates={candidates}
          onLoadCandidates={loadCandidates}
          receipts={receipts}
          transactions={transactions}
          accounts={refAccounts}
          expenseTypes={expenseTypes}
          expenseSubTypes={expenseSubTypes}
          vendors={refVendors}
          projects={refProjects}
          departments={refDepartments}
        />
      )}

      {/* ==================== BULK EDIT MODAL ==================== */}
      <Dialog open={bulkEditModal} onOpenChange={setBulkEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedTxns.size} Transactions</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Only non-empty fields will be applied.</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Business / Personal</label>
              <Select value={bulkForm.business_personal || ''} onValueChange={v => setBulkForm(p => ({ ...p, business_personal: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Business">Business</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Account Paid From</label>
              <Select value={bulkForm.account_paid_from || ''} onValueChange={v => setBulkForm(p => ({ ...p, account_paid_from: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  {refAccounts.map(a => <SelectItem key={a.account_id || a.name} value={a.name}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Expense Type</label>
              <Select value={bulkForm.expense_type || ''} onValueChange={v => setBulkForm(p => ({ ...p, expense_type: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  {expenseTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Status</label>
              <Select value={bulkForm.status || ''} onValueChange={v => setBulkForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbox">Inbox</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="matched">Matched</SelectItem>
                  <SelectItem value="ready_to_sync">Ready to Sync</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditModal(false)}>Cancel</Button>
            <Button onClick={() => {
              const updates = {};
              Object.entries(bulkForm).forEach(([k, v]) => { if (v) updates[k] = v; });
              if (Object.keys(updates).length) bulkUpdateTransactions(updates);
              else toast.error('No changes selected');
            }} data-testid="bulk-apply-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== COLUMN MAP MODAL ==================== */}
      {columnMapModal && (
        <ColumnMapModal
          statement={columnMapModal}
          onClose={() => setColumnMapModal(null)}
          workspaceId={activeWorkspace.workspace_id}
          onComplete={() => { setColumnMapModal(null); refreshAll(); }}
        />
      )}

      {/* ==================== MOBILE SESSION QR MODAL ==================== */}
      <Dialog open={mobileSessionModal} onOpenChange={closeMobileSession}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5" />Mobile Receipt Capture</DialogTitle>
          </DialogHeader>
          {activeSession && (
            <div className="space-y-4 text-center">
              <p className="text-xs text-muted-foreground">Scan this QR code on your phone to start capturing receipts. They'll appear here in real-time.</p>
              <div className="bg-white p-4 rounded-xl inline-block mx-auto border">
                <QRCodeSVG value={getMobileCaptureUrl()} size={200} level="M" />
              </div>
              <div className="space-y-1">
                <p className="text-[10px] text-muted-foreground">Or open this link on your phone:</p>
                <div className="flex items-center gap-1">
                  <input
                    readOnly
                    value={getMobileCaptureUrl()}
                    className="flex-1 h-7 text-[10px] px-2 border rounded bg-muted/30 font-mono"
                    onClick={e => { e.target.select(); navigator.clipboard?.writeText(getMobileCaptureUrl()); toast.success('Link copied'); }}
                    data-testid="mobile-capture-url"
                  />
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Session</span>
                  <span className="font-medium">{activeSession.name}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Receipts captured</span>
                  <span className="font-bold text-emerald-600">{activeSession.receipt_count || 0}</span>
                </div>
                <div className="flex items-center justify-center gap-1.5 mt-2">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[10px] text-green-600 font-medium">Live — watching for new receipts</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeMobileSession} className="w-full" data-testid="close-mobile-session-btn">
              Done Capturing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== TRANSACTION TABLE ====================
function TransactionTable({ transactions, selected, onSelect, onView, onUpdate, onCategorize, onConfirm, onUnlink, onFindCandidates, accounts, expenseTypes, showMatchInfo, showActions, readOnly }) {
  if (!transactions.length) {
    return (
      <div className="border rounded-lg p-8 text-center" data-testid="empty-transactions">
        <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No transactions in this view</p>
      </div>
    );
  }

  const toggleAll = () => {
    if (selected.size === transactions.length) onSelect(new Set());
    else onSelect(new Set(transactions.map(t => t.transaction_id)));
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelect(next);
  };

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="transaction-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/40 border-b">
              {!readOnly && (
                <th className="p-2 w-8"><input type="checkbox" checked={selected.size === transactions.length && transactions.length > 0} onChange={toggleAll} className="rounded" /></th>
              )}
              <th className="p-2 text-left font-medium">Date</th>
              <th className="p-2 text-left font-medium">Description</th>
              <th className="p-2 text-right font-medium">Amount</th>
              <th className="p-2 text-left font-medium">Account</th>
              <th className="p-2 text-left font-medium">Biz/Personal</th>
              <th className="p-2 text-left font-medium">Status</th>
              {showMatchInfo && <th className="p-2 text-left font-medium">Match</th>}
              <th className="p-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(t => (
              <tr key={t.transaction_id} className="border-b hover:bg-muted/20 transition-colors" data-testid={`txn-row-${t.transaction_id}`}>
                {!readOnly && (
                  <td className="p-2"><input type="checkbox" checked={selected.has(t.transaction_id)} onChange={() => toggle(t.transaction_id)} className="rounded" /></td>
                )}
                <td className="p-2 whitespace-nowrap">{t.date || '—'}</td>
                <td className="p-2 max-w-[250px] truncate" title={t.description}>{t.description || '—'}</td>
                <td className="p-2 text-right font-mono whitespace-nowrap">
                  {t.currency && <span className="text-muted-foreground mr-1">{t.currency}</span>}
                  {t.amount || '—'}
                </td>
                <td className="p-2">
                  {!readOnly ? (
                    <select value={t.account_paid_from || ''} onChange={e => onUpdate(t.transaction_id, { account_paid_from: e.target.value })}
                      className="h-6 text-[11px] bg-transparent border rounded px-1 max-w-[120px]">
                      <option value="">—</option>
                      {accounts.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
                    </select>
                  ) : (
                    <span className="text-[11px]">{t.account_paid_from || '—'}</span>
                  )}
                </td>
                <td className="p-2">
                  {!readOnly ? (
                    <select value={t.business_personal || ''} onChange={e => onUpdate(t.transaction_id, { business_personal: e.target.value })}
                      className="h-6 text-[11px] bg-transparent border rounded px-1">
                      <option value="">—</option>
                      <option value="Business">Business</option>
                      <option value="Personal">Personal</option>
                    </select>
                  ) : (
                    <span className="text-[11px]">{t.business_personal || '—'}</span>
                  )}
                </td>
                <td className="p-2"><StatusBadge status={t.status} /></td>
                {showMatchInfo && (
                  <td className="p-2"><MatchBadge status={t.match_status} score={t.match_score} /></td>
                )}
                <td className="p-2">
                  <div className="flex gap-0.5">
                    <button onClick={() => onView(t)} className="p-1 rounded hover:bg-muted" title="View details"><Eye className="w-3.5 h-3.5" /></button>
                    {!readOnly && onCategorize && (
                      <button onClick={() => onCategorize(t.transaction_id)} className="p-1 rounded hover:bg-muted" title="AI categorize"><Brain className="w-3.5 h-3.5" /></button>
                    )}
                    {showActions && onConfirm && t.matched_receipt_id && t.match_status !== 'confirmed' && (
                      <button onClick={() => onConfirm(t.transaction_id)} className="p-1 rounded hover:bg-muted text-green-600" title="Confirm match">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {showActions && onUnlink && t.matched_receipt_id && (
                      <button onClick={() => onUnlink(t.transaction_id)} className="p-1 rounded hover:bg-muted text-red-500" title="Unlink match">
                        <Unlink className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {showActions && onFindCandidates && (
                      <button onClick={() => onFindCandidates(t)} className="p-1 rounded hover:bg-muted text-blue-600" title="Find matches">
                        <Search className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!readOnly && !showActions && t.status !== 'ready_to_sync' && t.status !== 'synced' && (
                      <button onClick={() => onUpdate(t.transaction_id, { status: 'ready_to_sync' })} className="p-1 rounded hover:bg-muted text-green-600" title="Mark ready">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== RECEIPT GRID ====================
function ReceiptGrid({ receipts, onView, onExtract, loading }) {
  if (!receipts.length) {
    return (
      <div className="border rounded-lg p-8 text-center" data-testid="empty-receipts">
        <Receipt className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No receipts in this view</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="receipt-grid">
      {receipts.map(r => (
        <div key={r.receipt_id} className="border rounded-lg overflow-hidden hover:border-primary/50 transition-colors group cursor-pointer"
          onClick={() => onView(r)} data-testid={`receipt-card-${r.receipt_id}`}>
          <div className="aspect-[3/4] bg-muted/30 flex items-center justify-center relative">
            {r.file_type && ['jpg', 'jpeg', 'png', 'webp'].includes(r.file_type) ? (
              <img src={`${API}${r.file_url}`} alt={r.filename} className="object-cover w-full h-full" />
            ) : (
              <FileText className="w-10 h-10 text-muted-foreground" />
            )}
            <div className="absolute top-1 right-1 flex gap-0.5">
              {r.is_duplicate && (
                <span className="px-1.5 py-0 rounded-full bg-red-100 text-red-700 text-[9px] font-bold">DUP</span>
              )}
              <StatusBadge status={r.extraction_status === 'done' ? 'processed' : r.status} />
            </div>
            {r.quality_warnings?.length > 0 && (
              <div className="absolute bottom-1 left-1">
                <span className="px-1.5 py-0 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold flex items-center gap-0.5">
                  <AlertCircle className="w-2.5 h-2.5" />{r.quality_warnings.length}
                </span>
              </div>
            )}
          </div>
          <div className="p-2 space-y-1">
            <p className="text-[11px] font-medium truncate">{r.extracted_data?.merchant_name || r.filename}</p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">
                {r.extracted_data?.total ? `${r.extracted_data.currency || ''} ${r.extracted_data.total}` : 'Not extracted'}
              </span>
              {r.extraction_status === 'pending' && (
                <button onClick={(e) => { e.stopPropagation(); onExtract(r.receipt_id); }}
                  className="text-[10px] text-blue-600 hover:underline font-medium" disabled={loading}>
                  Extract
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== DETAIL MODAL ====================
function DetailModal({ type, item, onClose, onUpdate, onExtract, onConfirm, onUnlink, onLink, candidates, onLoadCandidates, receipts, transactions, accounts, expenseTypes, expenseSubTypes, vendors, projects, departments }) {
  if (!item) return null;

  // Candidates view — side-by-side matching
  if (type === 'candidates') {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Search className="w-5 h-5" />Match Candidates</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            {/* Left: Transaction */}
            <div className="space-y-3">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase">Transaction</p>
              <div className="bg-muted/30 rounded-lg p-3 space-y-1.5">
                <p className="text-xs"><strong>{item.date || '—'}</strong></p>
                <p className="text-xs">{item.description || '—'}</p>
                <p className="text-sm font-bold">{item.currency} {item.amount}</p>
                {item.reference && <p className="text-[10px] text-muted-foreground">Ref: {item.reference}</p>}
              </div>
              {item.matched_receipt_id && (
                <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 rounded p-2">
                  Currently matched to a receipt. Linking a new one will replace it.
                </div>
              )}
            </div>
            {/* Right: Candidates */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase">Receipt Candidates</p>
                <Button size="sm" variant="ghost" onClick={() => onLoadCandidates(item.transaction_id)}>
                  <RefreshCw className="w-3 h-3 mr-1" />Refresh
                </Button>
              </div>
              {!candidates?.candidates?.length ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No matching candidates found</p>
              ) : (
                <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                  {candidates.candidates.map(c => (
                    <div key={c.receipt_id} className="border rounded-lg p-3 hover:border-primary/50 transition-colors" data-testid={`candidate-${c.receipt_id}`}>
                      <div className="flex gap-3">
                        {c.file_url && ['jpg', 'jpeg', 'png', 'webp'].includes(c.file_type) ? (
                          <img src={`${API}${c.file_url}`} alt="" className="w-16 h-20 rounded border object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-16 h-20 rounded border bg-muted/30 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold truncate">{c.merchant_name || c.filename}</p>
                            <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-bold", c.score >= 70 ? "bg-green-100 text-green-700" : c.score >= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600")}>
                              {c.score}%
                            </span>
                          </div>
                          <p className="text-xs mt-0.5">{c.currency} {c.total} — {c.receipt_date || 'No date'}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.reasons.map((r, i) => (
                              <span key={i} className="px-1.5 py-0 rounded bg-muted text-[9px]">{r}</span>
                            ))}
                          </div>
                          {c.match_status !== 'unmatched' && c.match_status !== 'processed' && (
                            <p className="text-[10px] text-amber-600 mt-1">Already {c.match_status}</p>
                          )}
                        </div>
                        <Button size="sm" className="flex-shrink-0 self-center" onClick={() => onLink(item.transaction_id, c.receipt_id)} data-testid={`link-${c.receipt_id}`}>
                          <Link2 className="w-3 h-3 mr-1" />Link
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (type === 'transaction') {
    const matchedReceipt = item.matched_receipt_id ? receipts.find(r => r.receipt_id === item.matched_receipt_id) : null;
    const suggestion = item.category_suggestion || {};

    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5" />Transaction Detail</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {/* Left: Transaction Info */}
            <div className="space-y-3">
              <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Date</span>
                  <span className="text-xs font-medium">{item.date || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Description</span>
                  <span className="text-xs font-medium text-right max-w-[200px]">{item.description || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Amount</span>
                  <span className="text-xs font-bold">{item.currency} {item.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Reference</span>
                  <span className="text-xs">{item.reference || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Account</span>
                  <span className="text-xs">{item.account_paid_from || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Status</span>
                  <StatusBadge status={item.status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">Match</span>
                  <MatchBadge status={item.match_status} score={item.match_score} />
                </div>
              </div>
              {item.match_reasons?.length > 0 && (
                <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-green-700 dark:text-green-300 uppercase mb-1">Match Reasons</p>
                  {item.match_reasons.map((r, i) => (
                    <p key={i} className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" />{r}
                    </p>
                  ))}
                </div>
              )}
              {/* Match Action Buttons */}
              <div className="flex gap-2">
                {item.matched_receipt_id && item.match_status !== 'confirmed' && onConfirm && (
                  <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => { onConfirm(item.transaction_id); onClose(); }} data-testid="confirm-match-btn">
                    <Check className="w-3.5 h-3.5 mr-1" />Confirm Match
                  </Button>
                )}
                {item.matched_receipt_id && onUnlink && (
                  <Button size="sm" variant="outline" className="flex-1 text-red-600 hover:text-red-700" onClick={() => { onUnlink(item.transaction_id); onClose(); }} data-testid="unlink-match-btn">
                    <Unlink className="w-3.5 h-3.5 mr-1" />Unlink
                  </Button>
                )}
                {onLoadCandidates && (
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => onLoadCandidates(item.transaction_id)} data-testid="find-candidates-btn">
                    <Search className="w-3.5 h-3.5 mr-1" />Find Matches
                  </Button>
                )}
              </div>
              {Object.keys(suggestion).length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 uppercase mb-1">AI Suggestion</p>
                  {suggestion.expense_type && <p className="text-xs">Type: <strong>{suggestion.expense_type}</strong></p>}
                  {suggestion.vendor && <p className="text-xs">Vendor: <strong>{suggestion.vendor}</strong></p>}
                  {suggestion.business_personal && <p className="text-xs">Class: <strong>{suggestion.business_personal}</strong></p>}
                  {suggestion.confidence && <p className="text-xs text-muted-foreground">Confidence: {suggestion.confidence}%</p>}
                  {suggestion.reasoning && <p className="text-[10px] text-muted-foreground mt-1">{suggestion.reasoning}</p>}
                </div>
              )}
            </div>
            {/* Right: Matched Receipt */}
            <div className="space-y-3">
              {matchedReceipt ? (
                <div className="border rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold">Matched Receipt</p>
                  {matchedReceipt.file_url && ['jpg', 'jpeg', 'png', 'webp'].includes(matchedReceipt.file_type) ? (
                    <img src={`${API}${matchedReceipt.file_url}`} alt="Receipt" className="w-full rounded border max-h-[300px] object-contain" />
                  ) : (
                    <div className="bg-muted/30 rounded p-4 text-center"><FileText className="w-10 h-10 mx-auto text-muted-foreground" /><p className="text-xs mt-1">{matchedReceipt.filename}</p></div>
                  )}
                  {matchedReceipt.extracted_data && (
                    <div className="text-xs space-y-1">
                      {matchedReceipt.extracted_data.merchant_name && <p>Merchant: <strong>{matchedReceipt.extracted_data.merchant_name}</strong></p>}
                      {matchedReceipt.extracted_data.total && <p>Total: <strong>{matchedReceipt.extracted_data.currency} {matchedReceipt.extracted_data.total}</strong></p>}
                      {matchedReceipt.extracted_data.receipt_date && <p>Date: {matchedReceipt.extracted_data.receipt_date}</p>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="border rounded-lg p-8 text-center">
                  <Unlink className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">No receipt matched yet</p>
                </div>
              )}
              {item.raw_data && Object.keys(item.raw_data).length > 0 && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Raw Statement Data</p>
                  <div className="space-y-0.5">
                    {Object.entries(item.raw_data).map(([k, v]) => v && (
                      <div key={k} className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="text-right max-w-[200px] truncate">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Receipt detail
  if (type === 'receipt') {
    const d = item.extracted_data || {};
    const hasData = Object.keys(d).length > 0 && !d.error;
    const isNonEnglish = hasData && d.language && d.language !== 'en';

    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" />Receipt Detail</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              {item.file_url && ['jpg', 'jpeg', 'png', 'webp'].includes(item.file_type) ? (
                <img src={`${API}${item.file_url}`} alt="Receipt" className="w-full rounded border" />
              ) : (
                <div className="bg-muted/30 rounded p-8 text-center border">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground" />
                  <p className="text-xs mt-2">{item.filename}</p>
                </div>
              )}
              {item.extraction_status === 'pending' && (
                <Button size="sm" className="mt-2 w-full" onClick={() => onExtract(item.receipt_id)}>
                  <Zap className="w-3.5 h-3.5 mr-1.5" />Run AI Extraction
                </Button>
              )}
              {item.is_duplicate && (
                <div className="mt-2 bg-red-50 dark:bg-red-950/30 rounded-lg p-2 text-xs text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />Duplicate of {item.duplicate_of}
                </div>
              )}
              {item.quality_warnings?.length > 0 && (
                <div className="mt-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-2 space-y-0.5">
                  {item.quality_warnings.map((w, i) => (
                    <p key={i} className="text-[10px] text-amber-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{w}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <StatusBadge status={item.extraction_status === 'done' ? 'processed' : item.status} />
                <MatchBadge status={item.match_status} score={item.match_score} />
              </div>
              {hasData ? (
                <div className="space-y-3">
                  {/* Language badge */}
                  {d.language && (
                    <div className={cn("rounded-lg p-2 flex items-center gap-2", isNonEnglish ? "bg-blue-50 dark:bg-blue-950/30" : "bg-muted/30")}>
                      <span className="text-xs font-semibold">{d.language_name || d.language}</span>
                      {isNonEnglish && <span className="text-[10px] text-blue-600">Translated extraction</span>}
                      {d.country && <span className="ml-auto px-1.5 py-0 bg-muted rounded text-[10px] font-mono">{d.country}</span>}
                    </div>
                  )}

                  {/* Merchant */}
                  <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase">Merchant</p>
                    <p className="text-sm font-bold">{d.merchant_name || '—'}</p>
                    {isNonEnglish && d.merchant_name_local && d.merchant_name_local !== d.merchant_name && (
                      <p className="text-xs text-muted-foreground">{d.merchant_name_local}</p>
                    )}
                    {d.merchant_raw_text && d.merchant_raw_text !== d.merchant_name && (
                      <p className="text-[10px] text-muted-foreground font-mono">{d.merchant_raw_text}</p>
                    )}
                    {d.address && <p className="text-[10px] text-muted-foreground">{d.address}</p>}
                  </div>

                  {/* Amounts */}
                  <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase">Amounts</p>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Total</span>
                      <span className="font-bold text-base">{d.currency_symbol || d.currency || ''} {d.total}</span>
                    </div>
                    {d.subtotal > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Subtotal</span><span>{d.currency_symbol || ''} {d.subtotal}</span></div>}
                    {d.tax > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">{d.tax_label || 'Tax'}{d.tax_rate ? ` (${d.tax_rate}%)` : ''}</span><span>{d.currency_symbol || ''} {d.tax}</span></div>}
                    {d.service_charge > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Service Charge</span><span>{d.currency_symbol || ''} {d.service_charge}</span></div>}
                    {d.tip > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Tip</span><span>{d.currency_symbol || ''} {d.tip}</span></div>}
                  </div>

                  {/* Details */}
                  <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase">Details</p>
                    {d.receipt_date && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Date</span><span>{d.receipt_date}{d.receipt_time ? ` ${d.receipt_time}` : ''}</span></div>}
                    {d.payment_method && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Payment</span><span>{d.payment_method}</span></div>}
                    {d.card_last4 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Card</span><span className="font-mono">****{d.card_last4}</span></div>}
                    {d.transaction_reference && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Reference</span><span className="font-mono">{d.transaction_reference}</span></div>}
                    {d.currency && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Currency</span><span>{d.currency}{d.currency_symbol ? ` (${d.currency_symbol})` : ''}</span></div>}
                  </div>

                  {/* Line Items */}
                  {d.line_items?.length > 0 && (
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Line Items</p>
                      <div className="space-y-1">
                        {d.line_items.map((li, i) => (
                          <div key={i} className="flex justify-between text-[10px] gap-2">
                            <div className="min-w-0 flex-1">
                              <span>{isNonEnglish && li.description_en ? li.description_en : li.description}</span>
                              {isNonEnglish && li.description_en && li.description !== li.description_en && (
                                <span className="text-muted-foreground ml-1">({li.description})</span>
                              )}
                            </div>
                            {li.quantity && <span className="text-muted-foreground whitespace-nowrap">x{li.quantity}</span>}
                            <span className="font-mono whitespace-nowrap">{li.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Extraction Notes */}
                  {d.extraction_notes && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-2">
                      <p className="text-[10px] text-amber-700">{d.extraction_notes}</p>
                    </div>
                  )}

                  {/* Confidence & Raw Text */}
                  <div className="flex items-center justify-between pt-1 border-t">
                    {d.confidence && <p className="text-[10px] text-muted-foreground">AI Confidence: <strong>{d.confidence}%</strong></p>}
                    {d.raw_text_sample && (
                      <details className="text-[10px] text-muted-foreground">
                        <summary className="cursor-pointer hover:text-foreground">Raw text</summary>
                        <p className="mt-1 font-mono bg-muted/50 rounded p-1.5 max-h-20 overflow-y-auto">{d.raw_text_sample}</p>
                      </details>
                    )}
                  </div>
                </div>
              ) : d.error ? (
                <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-3">
                  <p className="text-xs text-red-600">{d.error}</p>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No data extracted yet</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return null;
}

// ==================== COLUMN MAP MODAL ====================
function ColumnMapModal({ statement, onClose, workspaceId, onComplete }) {
  const [mapping, setMapping] = useState({});
  const stdFields = ['date', 'description', 'amount', 'currency', 'reference', 'balance', 'card_last4', 'debit_credit'];
  const headers = statement?.headers || [];

  const save = async () => {
    const nonEmpty = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
    if (Object.keys(nonEmpty).length === 0) { onComplete(); return; }
    try {
      await fetch(`${API}/api/expense-capture/${workspaceId}/statements/${statement.statement_id}/remap`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapping: nonEmpty }),
      });
      toast.success('Column mapping applied');
      onComplete();
    } catch { toast.error('Failed'); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Map Statement Columns</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Match detected columns to standard fields. Only fill in what applies.</p>
        <div className="space-y-2 mt-2">
          {stdFields.map(f => (
            <div key={f} className="flex items-center gap-3">
              <span className="text-xs w-28 capitalize font-medium">{f.replace(/_/g, ' ')}</span>
              <select value={mapping[f] || ''} onChange={e => setMapping(p => ({ ...p, [f]: e.target.value }))}
                className="flex-1 h-7 text-xs bg-background border rounded px-2">
                <option value="">— skip —</option>
                {headers.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Skip</Button>
          <Button size="sm" onClick={save}>Apply Mapping</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== CONFIG PANEL ====================
function ConfigPanel({ accounts, categories, vendors, projects, departments, businessUnits, statements, memoryEntries, captureConfig, onDeleteMemory, onResetMemory, onSaveConfig }) {
  const [thresholds, setThresholds] = useState({});

  useEffect(() => {
    if (captureConfig) {
      setThresholds({
        auto_match_threshold: captureConfig.auto_match_threshold ?? 70,
        review_threshold: captureConfig.review_threshold ?? 30,
        auto_category_threshold: captureConfig.auto_category_threshold ?? 60,
        memory_enabled: captureConfig.memory_enabled ?? true,
      });
    }
  }, [captureConfig]);

  const PATTERN_LABELS = {
    description_merchant: 'Description → Merchant',
    merchant_vendor: 'Merchant → Vendor',
    merchant_category: 'Merchant → Category',
    card_account: 'Card Last4 → Account',
    merchant_business_personal: 'Merchant → Biz/Personal',
  };

  const RefList = ({ title, items, icon: Icon }) => (
    <div className="border rounded-lg p-3">
      <h4 className="text-xs font-semibold flex items-center gap-1.5 mb-2"><Icon className="w-3.5 h-3.5" />{title}</h4>
      {items.length === 0 ? (
        <p className="text-[10px] text-muted-foreground">None configured. Add in Finance &gt; Reference Data.</p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.map((item, i) => (
            <span key={i} className="px-2 py-0.5 bg-muted rounded text-[10px]">{item.name || item}</span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="config-panel">
      {/* Confidence Thresholds */}
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Settings2 className="w-4 h-4" />Matching Thresholds</h3>
        <p className="text-xs text-muted-foreground mb-3">Control how strict the reconciliation engine is when matching receipts to transactions.</p>
        <div className="grid grid-cols-3 gap-4">
          <div className="border rounded-lg p-3">
            <label className="text-[10px] text-muted-foreground uppercase font-semibold block mb-2">Auto-Match Threshold</label>
            <div className="flex items-center gap-2">
              <input type="range" min="50" max="100" value={thresholds.auto_match_threshold || 70}
                onChange={e => setThresholds(p => ({ ...p, auto_match_threshold: Number(e.target.value) }))}
                className="flex-1" data-testid="auto-threshold-slider" />
              <span className="text-sm font-bold w-10 text-right">{thresholds.auto_match_threshold || 70}%</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Matches above this score are auto-confirmed</p>
          </div>
          <div className="border rounded-lg p-3">
            <label className="text-[10px] text-muted-foreground uppercase font-semibold block mb-2">Review Threshold</label>
            <div className="flex items-center gap-2">
              <input type="range" min="10" max="70" value={thresholds.review_threshold || 30}
                onChange={e => setThresholds(p => ({ ...p, review_threshold: Number(e.target.value) }))}
                className="flex-1" data-testid="review-threshold-slider" />
              <span className="text-sm font-bold w-10 text-right">{thresholds.review_threshold || 30}%</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Matches above this go to review queue</p>
          </div>
          <div className="border rounded-lg p-3">
            <label className="text-[10px] text-muted-foreground uppercase font-semibold block mb-2">Memory Influence</label>
            <div className="flex items-center gap-2 mt-1">
              <button onClick={() => setThresholds(p => ({ ...p, memory_enabled: !p.memory_enabled }))}
                className={cn("w-10 h-5 rounded-full transition-colors relative",
                  thresholds.memory_enabled ? "bg-green-500" : "bg-muted")}
                data-testid="memory-toggle">
                <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform",
                  thresholds.memory_enabled ? "left-5" : "left-0.5")} />
              </button>
              <span className="text-xs">{thresholds.memory_enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Use learned patterns to improve matches</p>
          </div>
        </div>
        <Button size="sm" className="mt-3" onClick={() => onSaveConfig(thresholds)} data-testid="save-config-btn">
          Save Thresholds
        </Button>
      </div>

      {/* Memory Entries */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Brain className="w-4 h-4" />Learned Patterns ({memoryEntries.length})</h3>
          {memoryEntries.length > 0 && (
            <Button size="sm" variant="destructive" onClick={onResetMemory} data-testid="reset-memory-btn">
              <Trash2 className="w-3 h-3 mr-1" />Reset All
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">Patterns learned from confirmed matches. These improve future reconciliation accuracy.</p>
        {memoryEntries.length === 0 ? (
          <div className="border rounded-lg p-6 text-center">
            <Brain className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-xs text-muted-foreground">No patterns learned yet. Confirm matches to build memory.</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-muted/40 border-b">
                <th className="p-2 text-left font-medium">Type</th>
                <th className="p-2 text-left font-medium">Pattern</th>
                <th className="p-2 text-left font-medium">Maps To</th>
                <th className="p-2 text-right font-medium">Confirmed</th>
                <th className="p-2 w-8"></th>
              </tr></thead>
              <tbody>
                {memoryEntries.map(m => (
                  <tr key={m.memory_id} className="border-b hover:bg-muted/20" data-testid={`memory-${m.memory_id}`}>
                    <td className="p-2"><span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">{PATTERN_LABELS[m.pattern_type] || m.pattern_type}</span></td>
                    <td className="p-2 font-mono text-[11px] max-w-[200px] truncate">{m.pattern_key}</td>
                    <td className="p-2 font-medium">{m.pattern_value}</td>
                    <td className="p-2 text-right font-bold">{m.confirmed_count}x</td>
                    <td className="p-2">
                      <button onClick={() => onDeleteMemory(m.memory_id)} className="p-1 rounded hover:bg-red-100 text-red-500" title="Delete">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Controlled Lists */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Controlled Lists (from Orka Finance)</h3>
        <p className="text-xs text-muted-foreground mb-3">
          These lists are managed in Finance &gt; Reference Data. The applet uses them for controlled categorization — AI will only suggest values from these lists.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <RefList title="Accounts (Paid From)" items={accounts} icon={CreditCard} />
          <RefList title="Expense Categories" items={categories} icon={Tag} />
          <RefList title="Vendors / Suppliers" items={vendors} icon={Building2} />
          <RefList title="Projects" items={projects} icon={FolderOpen} />
          <RefList title="Departments" items={departments} icon={Users} />
          <RefList title="Business Units" items={businessUnits} icon={Building2} />
        </div>
      </div>

      {/* Imported Statements */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Imported Statements</h3>
        {statements.length === 0 ? (
          <p className="text-xs text-muted-foreground">No statements imported yet.</p>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-muted/40 border-b">
                <th className="p-2 text-left font-medium">File</th>
                <th className="p-2 text-left font-medium">Type</th>
                <th className="p-2 text-left font-medium">Account</th>
                <th className="p-2 text-right font-medium">Transactions</th>
                <th className="p-2 text-left font-medium">Uploaded</th>
              </tr></thead>
              <tbody>
                {statements.map(s => (
                  <tr key={s.statement_id} className="border-b">
                    <td className="p-2">{s.filename}</td>
                    <td className="p-2 uppercase">{s.file_type}</td>
                    <td className="p-2">{s.account_paid_from || '—'}</td>
                    <td className="p-2 text-right">{s.transaction_count}</td>
                    <td className="p-2 text-muted-foreground">{s.uploaded_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
